import { router } from "@inertiajs/react"
import { useEffect, useRef, useState } from "react"

import { getActionCableConsumer } from "@/lib/action-cable/consumer"
import { realtimeRouteFor } from "@/lib/realtime-routes"

type ConnectionMode = "realtime" | "polling"

type LocationRealtimeMessage = {
  eventId: number
  locationId: number
  aggregateType: string
  aggregateId: number
  action: string
  invalidate: string[]
  /**
   * Whether this event can have produced a staff notification. Optional on
   * purpose: during a deploy an already-open page runs the previous bundle, and
   * requiring the field in the guard below would make every message from the
   * new server unreadable to it. Absent reads as false, which costs a missed
   * dot, not a missed reload.
   */
  notify?: boolean
  occurredAt: string
}

type UseLocationRealtimeOptions = {
  enabled: boolean
  locationId?: number | null
  path: string
  debounceMs?: number
  pollingIntervalMs?: number
  connectionGracePeriodMs?: number
}

const DEFAULT_DEBOUNCE_MS = 250
const DEFAULT_POLLING_INTERVAL_MS = 5000
const DEFAULT_CONNECTION_GRACE_PERIOD_MS = 3000

// What this path re-fetches when something moves. See `lib/realtime-routes` for
// why the answer is two lists rather than one.
function pathReloadTargets(path: string): string[] {
  return realtimeRouteFor(path).reload
}

function shouldReload(message: LocationRealtimeMessage, path: string): boolean {
  // Matched against `watch`, never `reload`. The broadcaster publishes the
  // server's vocabulary, and on `/kds` the prop this page reloads (`tickets`)
  // is a word no broadcast has ever carried.
  const watched = realtimeRouteFor(path).watch
  if (watched.length === 0) return false

  return message.invalidate.some((key) => watched.includes(key))
}

function isLocationRealtimeMessage(data: unknown): data is LocationRealtimeMessage {
  if (typeof data !== "object" || data === null) return false

  const message = data as Partial<LocationRealtimeMessage>

  return (
    typeof message.eventId === "number" &&
    typeof message.locationId === "number" &&
    typeof message.aggregateType === "string" &&
    typeof message.aggregateId === "number" &&
    typeof message.action === "string" &&
    Array.isArray(message.invalidate) &&
    message.invalidate.every((key) => typeof key === "string") &&
    typeof message.occurredAt === "string"
  )
}

export function useLocationRealtime({
  enabled,
  locationId,
  path,
  debounceMs = DEFAULT_DEBOUNCE_MS,
  pollingIntervalMs = DEFAULT_POLLING_INTERVAL_MS,
  connectionGracePeriodMs = DEFAULT_CONNECTION_GRACE_PERIOD_MS,
}: UseLocationRealtimeOptions) {
  const [connectionMode, setConnectionMode] = useState<ConnectionMode>("realtime")
  /**
   * The id of the newest event that may carry a staff notification, and the
   * whole reason the notification feed no longer needs a connection of its own:
   * this subscription is already open and already hears about it.
   */
  const [notifySignal, setNotifySignal] = useState<number>(0)
  const pathRef = useRef(path)
  const subscriptionRef = useRef<{ unsubscribe: () => void } | null>(null)
  const reloadKeysRef = useRef<Set<string>>(new Set())
  const reloadTimerRef = useRef<number | null>(null)
  const pollingTimerRef = useRef<number | null>(null)
  const fallbackTimerRef = useRef<number | null>(null)
  const hasConnectedRef = useRef(false)
  const needsResyncOnConnectRef = useRef(false)

  pathRef.current = path

  useEffect(() => {
    if (!enabled || !locationId) {
      return undefined
    }

    const consumer = getActionCableConsumer()

    const clearReloadTimer = () => {
      if (!reloadTimerRef.current) return

      window.clearTimeout(reloadTimerRef.current)
      reloadTimerRef.current = null
    }

    const flushReload = () => {
      const only = Array.from(reloadKeysRef.current)
      reloadKeysRef.current.clear()
      clearReloadTimer()

      if (only.length === 0) return

      router.reload({
        only,
      })
    }

    const scheduleReload = (keys: string[]) => {
      if (keys.length === 0) return

      keys.forEach((key) => reloadKeysRef.current.add(key))

      if (reloadTimerRef.current) return

      reloadTimerRef.current = window.setTimeout(() => {
        flushReload()
      }, debounceMs)
    }

    const stopPolling = () => {
      if (!pollingTimerRef.current) return

      window.clearInterval(pollingTimerRef.current)
      pollingTimerRef.current = null
    }

    const startPolling = () => {
      if (pollingTimerRef.current) return

      setConnectionMode("polling")
      scheduleReload(pathReloadTargets(pathRef.current))
      pollingTimerRef.current = window.setInterval(() => {
        scheduleReload(pathReloadTargets(pathRef.current))
      }, pollingIntervalMs)
    }

    const clearFallbackTimer = () => {
      if (!fallbackTimerRef.current) return

      window.clearTimeout(fallbackTimerRef.current)
      fallbackTimerRef.current = null
    }

    const scheduleFallback = () => {
      if (fallbackTimerRef.current) return

      fallbackTimerRef.current = window.setTimeout(() => {
        startPolling()
      }, connectionGracePeriodMs)
    }

    scheduleFallback()

    const subscription = consumer.subscriptions.create(
      {
        channel: "Staff::LocationChannel",
        location_id: locationId,
      },
      {
        connected() {
          clearFallbackTimer()
          stopPolling()
          setConnectionMode("realtime")

          if (hasConnectedRef.current || needsResyncOnConnectRef.current) {
            scheduleReload(pathReloadTargets(pathRef.current))
          }

          hasConnectedRef.current = true
          needsResyncOnConnectRef.current = false
        },
        disconnected() {
          needsResyncOnConnectRef.current = true
          scheduleFallback()
        },
        rejected() {
          needsResyncOnConnectRef.current = true
          startPolling()
        },
        received(data: unknown) {
          if (!isLocationRealtimeMessage(data)) return

          // Independent of the reload below: an entry request notifies the
          // floor and invalidates no list, and a table does the reverse.
          if (data.notify === true) {
            setNotifySignal((current) => Math.max(current, data.eventId))
          }

          if (!shouldReload(data, pathRef.current)) return

          scheduleReload(pathReloadTargets(pathRef.current))
        },
      }
    )

    subscriptionRef.current = subscription

    return () => {
      clearReloadTimer()
      clearFallbackTimer()
      stopPolling()
      subscription.unsubscribe()
      subscriptionRef.current = null
      reloadKeysRef.current.clear()
      hasConnectedRef.current = false
      needsResyncOnConnectRef.current = false
    }
  }, [connectionGracePeriodMs, debounceMs, enabled, locationId, pollingIntervalMs])

  return {
    connectionMode,
    notifySignal,
  }
}
